import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Search, X, Trophy, TrendingUp, PoundSterling, List, Rows3 } from "lucide-react";
import { Input } from "@/components/ui/input";
import EmployerProgressionCard from "@/components/app/EmployerProgressionCard";
import CuratedFirmsBrowser from "@/components/app/CuratedFirmsBrowser";
import { api } from "@/lib/api";

/**
 * EmployersIndex — the full landing for the accelerator-employers
 * dataset.
 *
 * Two modes:
 *   1. Browse mode (empty search) — country tabs + a single ranked
 *      card. UK / US datasets are meaningfully different (£ HESA
 *      LEO vs $ editorial per-degree) so we keep them separate here.
 *   2. Search mode (any typed query) — the country tabs collapse and
 *      we render BOTH countries' cards stacked, each filtered by
 *      the query. Someone typing "Google" gets a hit in US straight
 *      away, without having to guess which tab to try first.
 *
 * Accepts an optional `?q=` URL param so the Landing page's
 * "Where do you want to work?" input can deep-link straight into
 * the cross-country view with the query pre-filled.
 */
export default function EmployersIndex() {
  const [params] = useSearchParams();
  const initialQ = (params.get("q") || "").trim();
  const [country, setCountry] = useState("UK");
  const [search, setSearch] = useState(initialQ);
  const [sortBy, setSortBy] = useState("lift");
  // Density toggle — flips row layout between "full" (default, all
  // signals visible on desktop) and "compact" (name + inline chips
  // only) so mobile users can scan 20 firms per screen instead of 5.
  // Persist to localStorage so the choice survives page reloads.
  const [dense, setDense] = useState(() => {
    try { return localStorage.getItem("wua_employers_dense") === "1"; }
    catch { return false; }
  });
  const toggleDense = (next) => {
    setDense(next);
    try { localStorage.setItem("wua_employers_dense", next ? "1" : "0"); } catch {}
  };
  const isSearching = search.trim().length > 0;

  // Live count for the "How to get into…" CTA. Was hardcoded "132 guides"
  // which drifted the moment we seeded UAE firms (took the index from
  // 730 → 756). Falls back to a sensible default string if the fetch
  // fails so the CTA still reads naturally.
  const [guideCount, setGuideCount] = useState(null);
  useEffect(() => {
    let cancelled = false;
    api.get("/employers/curated?limit=1")
      .then(({ data }) => { if (!cancelled) setGuideCount(data?.total ?? null); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  return (
    <div data-testid="employers-index-page" className="max-w-5xl mx-auto px-6 py-10">
      <Link
        to="/"
        data-testid="employers-index-back"
        className="inline-flex items-center gap-1 text-sm text-stone-600 hover:text-[#4C1D95]"
      >
        <ArrowLeft className="h-3 w-3" /> Home
      </Link>
      <div className="mt-6 mb-8">
        <div className="label-uppercase text-stone-600">Career-accelerator employers</div>
        <h1 className="mt-2 font-editorial text-4xl sm:text-5xl leading-tight">
          Every graduate employer, ranked by career growth or cohort density.
        </h1>
        <p className="mt-4 text-stone-600 max-w-2xl leading-relaxed">
          <span className="font-medium">UK + US</span> firms are ranked by cohort salary lift
          (HESA LEO / editorial). The other 7 tier-A markets we cover — Ireland,
          Germany, France, Italy, Spain, Portugal and the UAE — are ranked by
          feeder-course density because central payroll data isn't published.
          Tap any firm to see its own
          <span className="font-medium"> published pay bands</span>, regional hub blurb
          and grad-scheme link.
        </p>
        <Link
          to="/firm-guides"
          data-testid="employers-hero-firm-guides-cta"
          className="mt-5 inline-flex items-center gap-1.5 rounded-full border border-[#4C1D95] bg-[#4C1D95]/[0.04] px-4 py-2 text-[13px] font-medium text-[#4C1D95] hover:bg-[#4C1D95] hover:text-white transition"
        >
          <Trophy className="h-4 w-4" />
          How to get into any of these firms — {guideCount ? `${guideCount.toLocaleString()} guides` : "every guide"}
          <ArrowRight className="h-3.5 w-3.5" />
        </Link>
      </div>

      {/* Live text filter — searches across both country datasets
          when active, so "Google" finds hits in US even if the UK
          tab is selected. Clear button restores browse mode. */}
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[240px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400 pointer-events-none" />
          <Input
            placeholder='Search all employers — e.g. "McKinsey", "Google", "Foreign"'
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="employers-index-search"
            className="pl-9 pr-9"
          />
          {search && (
            <button
              type="button"
              onClick={() => setSearch("")}
              data-testid="employers-index-search-clear"
              aria-label="Clear search"
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-700"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        {/* Sort toggle — flips the ranking between the default
            "growth (LEO 3y→5y lift)" view and a "firm-published
            NQ pay" view. Both lenses are legitimate but answer
            different questions ("who grows fastest?" vs
            "who pays most day-one?"). */}
        <div
          className="inline-flex rounded-full border border-stone-200 bg-white p-0.5 text-xs"
          role="tablist"
          aria-label="Sort employers"
          data-testid="employers-index-sort"
        >
          <button
            type="button"
            role="tab"
            aria-selected={sortBy === "lift"}
            onClick={() => setSortBy("lift")}
            data-testid="employers-index-sort-lift"
            className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 font-medium transition ${
              sortBy === "lift"
                ? "bg-[#4C1D95] text-white"
                : "text-stone-600 hover:text-[#4C1D95]"
            }`}
          >
            <TrendingUp className="h-3.5 w-3.5" /> Growth
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={sortBy === "nq"}
            onClick={() => setSortBy("nq")}
            data-testid="employers-index-sort-nq"
            className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 font-medium transition ${
              sortBy === "nq"
                ? "bg-[#4C1D95] text-white"
                : "text-stone-600 hover:text-[#4C1D95]"
            }`}
          >
            <PoundSterling className="h-3.5 w-3.5" /> NQ pay
          </button>
        </div>

        {/* Density toggle — full rows (default) vs compact rows so
            mobile users can scan ~20 firms per screen instead of 5.
            Hidden on the smallest screens where the chip row would
            already be wrapping. */}
        <div
          className="inline-flex rounded-full border border-stone-200 bg-white p-0.5 text-xs"
          role="tablist"
          aria-label="Row density"
          data-testid="employers-index-density"
        >
          <button
            type="button"
            role="tab"
            aria-selected={!dense}
            onClick={() => toggleDense(false)}
            data-testid="employers-index-density-full"
            title="Full rows"
            className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 font-medium transition ${
              !dense
                ? "bg-[#4C1D95] text-white"
                : "text-stone-600 hover:text-[#4C1D95]"
            }`}
          >
            <List className="h-3.5 w-3.5" /> Full
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={dense}
            onClick={() => toggleDense(true)}
            data-testid="employers-index-density-compact"
            title="Compact rows"
            className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 font-medium transition ${
              dense
                ? "bg-[#4C1D95] text-white"
                : "text-stone-600 hover:text-[#4C1D95]"
            }`}
          >
            <Rows3 className="h-3.5 w-3.5" /> Compact
          </button>
        </div>
      </div>

      {/* Browse mode — single card with country tab switcher. */}
      {!isSearching && (
        <EmployerProgressionCard
          limit={100}
          minAppearances={3}
          testid="employers-index-card"
          country={country}
          showCountryTabs
          onCountryChange={setCountry}
          search={search}
          sortBy={sortBy}
          dense={dense}
        />
      )}

      {/* Search mode — parallel cards across all 8 tier-A markets so
          a query hits every ranking without the user picking a tab
          first. Each card hides its own error/empty gracefully
          (returns null when the filtered list is empty), so we render
          all unconditionally and let them decide. */}
      {isSearching && (
        <div
          className="space-y-6"
          data-testid="employers-index-cross-country"
        >
          {[
            { code: "UK", flag: "🇬🇧", label: "UK ranking" },
            { code: "US", flag: "🇺🇸", label: "US ranking" },
            { code: "IE", flag: "🇮🇪", label: "Ireland ranking" },
            { code: "DE", flag: "🇩🇪", label: "Germany ranking" },
            { code: "FR", flag: "🇫🇷", label: "France ranking" },
            { code: "IT", flag: "🇮🇹", label: "Italy ranking" },
            { code: "ES", flag: "🇪🇸", label: "Spain ranking" },
            { code: "PT", flag: "🇵🇹", label: "Portugal ranking" },
            // UAE added Feb 2026 — ranked by feeder-course density
            // (same model as the other non-UK/US markets) once the
            // `/api/employers/by-country?country=AE` route started
            // returning data. Users searching "Emirates", "ADNOC",
            // "G42" etc. should now get hits in this section.
            { code: "AE", flag: "🇦🇪", label: "UAE ranking" },
          ].map((c) => (
            <div key={c.code}>
              <div className="mb-2 text-xs font-underground-sc tracking-wider text-stone-500 flex items-center gap-1.5">
                <span aria-hidden>{c.flag}</span> {c.label}
              </div>
              <EmployerProgressionCard
                limit={100}
                minAppearances={3}
                testid={`employers-index-${c.code.toLowerCase()}-card`}
                country={c.code}
                search={search}
                sortBy={sortBy}
                dense={dense}
              />
            </div>
          ))}
        </div>
      )}

      <CuratedFirmsBrowser />
    </div>
  );
}
